/**
 * Onde a sessão do aluno é guardada, e por quanto tempo.
 *
 * São dois armazenamentos com propósitos diferentes:
 *
 *   localStorage   sobrevive a fechar o navegador — é o "continuar conectado"
 *   sessionStorage morre com a aba — é o padrão de computador compartilhado
 *
 * A escolha é do aluno, na tela de login, e precisa combinar com a validade do token
 * que o backend emite: guardar por 30 dias um token que expira em 12h só adiaria a
 * tela de login para o meio de uma aula.
 *
 * Este arquivo existe para que essa decisão fique em UM lugar. Antes, `localStorage`
 * aparecia solto no authSlice e no api.js, e trocar de armazenamento significaria caçar
 * cada chamada — deixando para trás justamente a que limpa a sessão no 401.
 */

const CHAVE_TOKEN = 'ndm_token';
const CHAVE_USUARIO = 'ndm_user';

// Navegador em janela privada, ou com cookies/site data bloqueados, lança ao tocar em
// storage. A sessão então vale só enquanto a aba viver — o que é degradação aceitável;
// derrubar o app inteiro não é.
const seguro = (fn, padrao = null) => {
  try {
    return fn();
  } catch {
    return padrao;
  }
};

const memoria = new Map();

const bancos = () => {
  const lista = [];
  seguro(() => lista.push(window.localStorage));
  seguro(() => lista.push(window.sessionStorage));
  return lista;
};

export function guardarSessao({ token, user, manterConectado }) {
  // Limpa dos DOIS antes de gravar: sem isso, quem entrou com "continuar conectado" e
  // depois entrou sem a opção deixaria o token antigo no localStorage, e ele voltaria a
  // valer na próxima visita — exatamente o contrário do que a pessoa pediu.
  limparSessao();
  const banco = seguro(() => (manterConectado ? window.localStorage : window.sessionStorage));
  if (!banco) {
    memoria.set(CHAVE_TOKEN, token);
    memoria.set(CHAVE_USUARIO, JSON.stringify(user));
    return;
  }
  banco.setItem(CHAVE_TOKEN, token);
  banco.setItem(CHAVE_USUARIO, JSON.stringify(user));
}

export function lerToken() {
  for (const banco of bancos()) {
    const t = seguro(() => banco.getItem(CHAVE_TOKEN));
    if (t) return t;
  }
  return memoria.get(CHAVE_TOKEN) || null;
}

export function lerUsuario() {
  for (const banco of bancos()) {
    const bruto = seguro(() => banco.getItem(CHAVE_USUARIO));
    if (!bruto) continue;
    try {
      return JSON.parse(bruto);
    } catch {
      // JSON corrompido derrubava o módulo inteiro no import e deixava o app numa tela
      // branca da qual nem dava para sair para o login.
      seguro(() => banco.removeItem(CHAVE_USUARIO));
    }
  }
  const emMemoria = memoria.get(CHAVE_USUARIO);
  return emMemoria ? seguro(() => JSON.parse(emMemoria)) : null;
}

export function limparSessao() {
  for (const banco of bancos()) {
    seguro(() => banco.removeItem(CHAVE_TOKEN));
    seguro(() => banco.removeItem(CHAVE_USUARIO));
  }
  memoria.clear();
}

/**
 * Atualiza só o usuário, no mesmo lugar onde o token já está.
 *
 * O perfil muda em uso — a pessoa preenche o CPF, a empresa — e o app regrava o usuário.
 * Gravar sempre no localStorage vazaria o dado para fora da sessão escolhida: quem entrou
 * sem "continuar conectado" fecharia o navegador e deixaria o próprio nome e e-mail na
 * máquina, sem token e sem forma de limpar pela interface.
 */
export function atualizarUsuario(user) {
  const json = JSON.stringify(user);
  for (const banco of bancos()) {
    if (seguro(() => banco.getItem(CHAVE_TOKEN))) {
      seguro(() => banco.setItem(CHAVE_USUARIO, json));
      return;
    }
  }
  if (memoria.has(CHAVE_TOKEN)) memoria.set(CHAVE_USUARIO, json);
}

/** Se a sessão atual foi aberta com "continuar conectado". Usado só para exibição. */
export function sessaoPersistente() {
  return !!seguro(() => window.localStorage.getItem(CHAVE_TOKEN));
}
